import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { testService } from '@/services/api/testService';
import { deinterleaveChannels } from '@/lib/impedance';
import type { TestChartData, RawImpedanceResult } from '@/lib/impedance';

const DEFAULT_CHANNEL_IDS = ['CH1', 'CH2', 'CH3', 'CH4'];

/**
 * Extracts channel IDs from the profile snapshot JSON stored on the spectra response.
 * Falls back to DEFAULT_CHANNEL_IDS if the snapshot is absent or unparseable.
 */
function extractChannelIds(profileSnapshotJson?: string): string[] {
  if (!profileSnapshotJson) return DEFAULT_CHANNEL_IDS;
  try {
    const snapshot = JSON.parse(profileSnapshotJson) as {
      channelCount?: number;
      channelConfigJson?: { channelId?: number }[];
    };
    if (snapshot.channelConfigJson && Array.isArray(snapshot.channelConfigJson)) {
      return snapshot.channelConfigJson.map((c, i) => `CH${(c.channelId ?? i + 1)}`);
    }
    if (typeof snapshot.channelCount === 'number' && snapshot.channelCount > 0) {
      return Array.from({ length: snapshot.channelCount }, (_, i) => `CH${i + 1}`);
    }
  } catch {
    // ignore
  }
  return DEFAULT_CHANNEL_IDS;
}

export function useChartData(selectedTestIds: string[]) {
  const enabled = selectedTestIds.length > 0;

  const { data: rawSpectra, isLoading, error } = useQuery({
    queryKey: ['batch-spectra', ...selectedTestIds.slice().sort()],
    queryFn: () => testService.getBatchSpectra(selectedTestIds),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const testData = useMemo<TestChartData[]>(() => {
    if (!rawSpectra) return [];

    return rawSpectra.map((spectra) => {
      const channelIds = extractChannelIds(spectra.profileSnapshotJson);

      let baseline = null;
      let specimen = null;

      try {
        const baselineRaw = spectra.baselineResultJson as RawImpedanceResult;
        baseline = deinterleaveChannels(baselineRaw, channelIds);
      } catch {
        // invalid data — leave baseline null
      }

      try {
        const specimenRaw = spectra.specimenResultJson as RawImpedanceResult;
        specimen = deinterleaveChannels(specimenRaw, channelIds);
      } catch {
        // invalid data — leave specimen null
      }

      const label =
        spectra.importSourceBaseline && spectra.importSourceSpecimen
          ? `${spectra.importSourceBaseline}/${spectra.importSourceSpecimen}`
          : spectra.testId.slice(0, 8);

      return {
        testId: spectra.testId,
        label,
        notes: spectra.notes ?? undefined,
        sourceBaseline: spectra.importSourceBaseline ?? undefined,
        sourceSpecimen: spectra.importSourceSpecimen ?? undefined,
        baseline,
        specimen,
      } satisfies TestChartData;
    });
  }, [rawSpectra]);

  return { testData, isLoading: enabled && isLoading, error };
}

/**
 * Generates a temporal color palette from cool (blue) → warm (red).
 * The first test gets blue (earliest), the last gets red (latest).
 */
export function generateTemporalPalette(count: number): string[] {
  return Array.from({ length: count }, (_, i) => {
    const hue = 240 - (240 * i) / Math.max(count - 1, 1);
    return `hsl(${Math.round(hue)}, 70%, 50%)`;
  });
}
